import React from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  ICondoDetails,
  ICondoUnit,
  ICondoUnits,
  ICondoUnitsSearch,
  CustomEvents,
} from 'src/common-types/index';
import { DATE_FORMAT_UNIT } from 'src/constants/index';
import { renderNumber, getNights, getReserveUnitUrl, events, formatDateCheckInOut, getDateCheckInOut, getDateCheckInOutISOString } from 'src/utils/index';
import { BlueButton, ModalCancellationPolicies } from 'src/components/components/index';
import { InfoSvg } from 'src/assets/index';
import { RootState } from 'src/store';
import {
  ICondoDetailsState,
  IUnitsSearchState,
  unitsSearchActions,
  condoDetailsActions,
} from 'src/store/slices/index';
import './style.scss';

interface IMapStateToProps {
  condoDetailsStore: ICondoDetailsState;
  unitsSearchStore: IUnitsSearchState;
}

interface IMapDispatchToProps {
  setDates: (dates: { startDate: Date; endDate: Date }) => void;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setDatesString: (dates: { startDate: string; endDate: string }) => void;
  setUpdateUnitsType: (item: ICondoDetails) => void;
  setCondoInfo: (item: ICondoUnitsSearch) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps {
  unit: ICondoUnit;
  condoId: number;
  isOnlyAlternative: boolean;
}

interface IState {
  isModalCancellationPoliciesVisible: boolean;
}

class CondoUnitRowComponent extends React.Component<IProps, IState> {
  state: IState = {
    isModalCancellationPoliciesVisible: false,
  };

  updateDatesAvailability = () => {
    const { condoDetailsStore, isOnlyAlternative, unit, unitsSearchStore } = this.props;
    const { condo } = condoDetailsStore;

    if (!isOnlyAlternative) {
      return;
    }

    if (unitsSearchStore.isSearchUnits && unitsSearchStore.condoInfo) {
      const test = {
        ...unitsSearchStore.condoInfo,
        units: this.changeMatchTypeForUnit(unit, unitsSearchStore.condoInfo.units),
      };

      this.props.setCondoInfo(test);
    } else {
      const updateUnitsType = {
        ...condo,
        condoDetails: {
          ...condo.condoDetails,
          units: this.changeMatchTypeForUnit(unit, condo.condoDetails.units),
        },
      };

      this.props.setUpdateUnitsType(updateUnitsType);
    }

    this.props.setStartDate(getDateCheckInOut(unit.dateRange.from));
    this.props.setEndDate(getDateCheckInOut(unit.dateRange.to));
    events.emit(CustomEvents.CondoStartEndDateChanged, {
      from: unit.dateRange.from,
      to: unit.dateRange.to,
    });
  };

  changeMatchTypeForUnit = (unit: ICondoUnit, units: ICondoUnits[]) => {
    return units.map((item) => {
      return {
        ...item,
        availibilities: item.availibilities.map((availibility) => {
          if (
            unit.dateRange.from === availibility.dateRange.from &&
            unit.dateRange.to === availibility.dateRange.to
          ) {
            return { ...availibility, matchType: 'exact' };
          }
          return availibility;
        }),
      };
    });
  };

  showCancellationPolicies = (): void => {
    this.setState({
      isModalCancellationPoliciesVisible: true,
    });
  };

  handleCancelModalCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: false });
  };

  render(): React.ReactNode {
    const { unit, condoId, isOnlyAlternative } = this.props;
    const { isModalCancellationPoliciesVisible } = this.state;
    const { dateRange, price, unitId, cancellationPolicyText } = unit;

    return (
      <div className="unit-row">
        <div className="unit-row__left-block">
          <p
            className={`unit-row__left-block-text ${isOnlyAlternative ? 'only-alternative' : ''}`}
            onClick={this.updateDatesAvailability}
          >
            {formatDateCheckInOut(dateRange.from, DATE_FORMAT_UNIT)} -{' '}
            {formatDateCheckInOut(dateRange.to, DATE_FORMAT_UNIT)}
          </p>
          <div
            className={`unit-row__left-block-text-mobile ${isOnlyAlternative ? 'only-alternative' : ''
              }`}
          >
            <p className="unit-row__date-title">
              <FormattedMessage id="check.in" />:
            </p>
            <p className="unit-row__date-info">
              {formatDateCheckInOut(dateRange.from, DATE_FORMAT_UNIT)}
            </p>
          </div>
          <div
            className={`unit-row__left-block-text-mobile ${isOnlyAlternative ? 'only-alternative' : ''
              }`}
          >
            <p className="unit-row__date-title">
              <FormattedMessage id="check.out" />:
            </p>
            <p className="unit-row__date-info">
              {formatDateCheckInOut(dateRange.to, DATE_FORMAT_UNIT)}
            </p>
          </div>
        </div>
        <div className="unit-row__right-block">
          <div className="unit-row__right-block-wrapper">
            <div className="unit-row__price">
              <div className="unit-row__price-total">
                <p className="unit-row__price-total-text">${renderNumber(Math.floor(price))}</p>
                <div className="unit-row__for-info">
                  <p className="unit-row__for-info-text">
                    <FormattedMessage id="for" />{' '}
                    <FormattedMessage
                      id="nights"
                      values={{ count: getNights(dateRange.from, dateRange.to) }}
                    />{' '}
                    <span className="unit-row__price-includes-taxes">
                      <FormattedMessage id="abbreviated.price.includes.taxes" />
                    </span>
                  </p>
                </div>
              </div>
            </div>
            {!isOnlyAlternative && (
              <div className="unit-row__refundable mobile">
                <div
                  className="unit-row__refundable-nonref"
                  onClick={this.showCancellationPolicies}
                >
                  <span>
                    <FormattedMessage id="non.refundable" />
                  </span>
                  <InfoSvg />
                </div>
              </div>
            )}
          </div>
          {!isOnlyAlternative && (
            <>
              <div className="unit-row__refundable">
                <div
                  className="unit-row__refundable-nonref"
                  onClick={this.showCancellationPolicies}
                >
                  <span>
                    <FormattedMessage id="non.refundable" />
                  </span>
                  <InfoSvg />
                </div>
              </div>
              <div className="unit-row__action">
                <Link to={getReserveUnitUrl(condoId, unitId)}>
                  <BlueButton>
                    <FormattedMessage id="reserve.unit" />
                  </BlueButton>
                </Link>
              </div>
            </>
          )}
        </div>
        <ModalCancellationPolicies
          isForCondo={true}
          cancellationPoliciesText={cancellationPolicyText}
          visible={isModalCancellationPoliciesVisible}
          onCancel={this.handleCancelModalCancellationPolicies}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    unitsSearchStore: state.unitsSearchStore,
    condoDetailsStore: state.condoDetailsStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  setDates: (dates: { startDate: Date; endDate: Date }) => {
    dispatch(
      unitsSearchActions.setDates({
        startDate: getDateCheckInOutISOString(dates.startDate),
        endDate: getDateCheckInOutISOString(dates.endDate),
      }),
    );
  },
  setDatesString: (dates: { startDate: string; endDate: string }) => {
    dispatch(
      unitsSearchActions.setDates({
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    );
  },
  setStartDate: (date: Date) => {
    dispatch(unitsSearchActions.setStartDate(getDateCheckInOutISOString(date)));
  },
  setEndDate: (date: Date) => {
    dispatch(unitsSearchActions.setEndDate(getDateCheckInOutISOString(date)));
  },
  setUpdateUnitsType: (item: ICondoDetails) => {
    dispatch(condoDetailsActions.setUpdateUnitsType(item));
  },
  setCondoInfo: (item: ICondoUnitsSearch) => {
    dispatch(unitsSearchActions.setCondoInfo(item));
  },
});

export const CondoUnitRow = connect(mapStateToProps, mapDispatchToProps)(CondoUnitRowComponent);
